// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Card, FormLayout, TextField, Button, TextStyle, Banner } from '@shopify/polaris';

import { app } from 'src/config/';
import { routes } from 'src/routes';
import { shopifyRedirect } from 'src/integrations/shopify-app-bridge/redirect';
import * as klaviyoActions from 'src/stores/actions/klaviyo-actions';

const MYSHOPIFY_SUFFIX = '.myshopify.com';
// Subdomain only: letters, numbers, hyphens (Shopify store handle).
const SUBDOMAIN_REGEX = /^[a-z0-9][a-z0-9-]*$/;
const APP_STORE_URL = 'https://apps.shopify.com/product-filter-search';

/**
 * Entry page for the Klaviyo embedded install flow (route `/klaviyo-integration/install`, aliased
 * "A" in planning.md) — reached when a merchant clicks Install from Klaviyo's app listing. The
 * merchant enters just the store subdomain; we show the fixed `.myshopify.com` suffix.
 *
 * Deep-link handoff design (see docs/klaviyo-embedded-install-login-flow.md §9 / planning.md). We
 * do NOT assume the merchant is logged into Shopify, and this page does NOT mint a JWT or build the
 * Klaviyo link itself. It only:
 *  1. Asks the backend `admin/klaviyo/oauth/install?shop=` (unauthenticated) whether the tenant
 *     already exists (`installed`).
 *  2. Not installed -> render a warning under the title pointing the merchant at the Shopify App
 *     Store listing; no redirect.
 *  3. Installed -> redirect to the Shopify admin deep-link
 *     `https://${shop}/admin/apps/${app.apiKey}/klaviyo-integration?autoConnect=1`. Shopify runs
 *     its own login if needed and then loads our embedded app at that path. The embedded `Main`
 *     shell mints the session token, and `klaviyo/index.jsx` (C) sees `autoConnect=1` and runs its
 *     existing `handleConnect` to start the Klaviyo OAuth. This page's job ends at the redirect.
 */
class KlaviyoInstall extends Component<WithI18nProps> {
  state = {
    subdomain: '',
    error: '',
    isLoading: false,
    notInstalled: false
  };

  handleChange = value => {
    // Keep only the subdomain even if someone pastes a full domain/URL.
    const cleaned = (value || '')
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/\.myshopify\.com.*$/, '')
      .replace(/\/.*$/, '');
    this.setState({ subdomain: cleaned, error: '', notInstalled: false });
  };

  handleSubmit = () => {
    const { i18n } = this.props;
    const subdomain = this.state.subdomain.trim().toLowerCase();

    if (!SUBDOMAIN_REGEX.test(subdomain)) {
      this.setState({
        error: i18n.translate('AdminHTML.KlaviyoIntegration.Install.domain_Invalid')
      });
      return;
    }

    const shop = `${subdomain}${MYSHOPIFY_SUFFIX}`;
    this.setState({ isLoading: true, error: '', notInstalled: false });

    console.log('[KlaviyoInstall] submitting, checking install status for', shop);
    this.checkInstallStatus(shop);
  };

  // Ask the backend whether Boost is installed on this shop, then either warn (not installed) or
  // hand off to Shopify's admin deep-link (installed). The endpoint never redirects — it just
  // returns data — so the navigation decision is ours.
  checkInstallStatus = async shop => {
    const { i18n, checkKlaviyoInstallStatus } = this.props;

    try {
      const result = await checkKlaviyoInstallStatus(shop);
      console.log('[KlaviyoInstall] install-check response', { shop, result });

      if (result.error) {
        // apiService/processRes already surfaced a toast — just stop spinning.
        this.setState({ isLoading: false });
        return;
      }

      const { installed, invalidShop } = _.get(result, 'payload.data', {});

      if (invalidShop) {
        this.setState({
          isLoading: false,
          error: i18n.translate('AdminHTML.KlaviyoIntegration.Install.domain_Invalid')
        });
        return;
      }

      if (!installed) {
        console.log('[KlaviyoInstall] Boost not installed on shop, showing warning', { shop });
        this.setState({ isLoading: false, notInstalled: true });
        return;
      }

      // Installed -> hand off to Shopify's admin deep-link. Shopify handles login (whether or not
      // the merchant is already signed in), then loads our embedded app at the Klaviyo page with
      // autoConnect=1, where C (klaviyo/index.jsx) auto-runs handleConnect.
      const deepLink = `https://${shop}/admin/apps/${app.apiKey}${routes.integration.klaviyo.view()}?autoConnect=1`;
      console.log('[KlaviyoInstall] Boost installed, redirecting to Shopify admin deep-link', {
        shop,
        deepLink
      });
      this.redirectTo(deepLink);
    } catch (e) {
      console.log('[KlaviyoInstall] install-check threw', { shop, error: e?.message });
      this.setState({
        isLoading: false,
        error: i18n.translate('AdminHTML.KlaviyoIntegration.Install.login_Failure')
      });
    }
  };

  redirectTo = url => {
    if (window.top === window.self) {
      window.open(url, '_top');
    } else {
      shopifyRedirect(url);
    }
  };

  handleKeyDown = e => {
    if (e.key === 'Enter') {
      this.handleSubmit();
    }
  };

  render() {
    const { i18n } = this.props;
    const { subdomain, error, isLoading, notInstalled } = this.state;

    return (
      <div
        className="klaviyo-install"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          boxSizing: 'border-box'
        }}>
        <div style={{ width: '100%', maxWidth: 440 }}>
          <Card
            sectioned
            title={i18n.translate('AdminHTML.KlaviyoIntegration.Install.login_Title')}>
            <FormLayout>
              {notInstalled && (
                <Banner status="warning">
                  <p>
                    {i18n.translate('AdminHTML.KlaviyoIntegration.Install.notInstalled_Warning', {
                      link: (
                        <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                          {i18n.translate('AdminHTML.KlaviyoIntegration.Install.notInstalled_Link')}
                        </a>
                      )
                    })}
                  </p>
                </Banner>
              )}
              <p>
                <TextStyle variation="subdued">
                  {i18n.translate('AdminHTML.KlaviyoIntegration.Install.login_Description')}
                </TextStyle>
              </p>
              <div onKeyDown={this.handleKeyDown}>
                <TextField
                  label={i18n.translate('AdminHTML.KlaviyoIntegration.Install.domain_Label')}
                  value={subdomain}
                  onChange={this.handleChange}
                  placeholder="your-store"
                  suffix={MYSHOPIFY_SUFFIX}
                  autoComplete="off"
                  error={error}
                />
              </div>
              <Button primary fullWidth loading={isLoading} onClick={this.handleSubmit}>
                {i18n.translate('AdminHTML.KlaviyoIntegration.Install.login_Action')}
              </Button>
            </FormLayout>
          </Card>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  checkKlaviyoInstallStatus: shop =>
    dispatch(klaviyoActions.checkKlaviyoInstallStatus(shop, ownProps.i18n))
});

export default withI18n()(connect(null, mapDispatchToProps)(KlaviyoInstall));
